/**
 * Minimum Order Gate - Trade Theme
 * 
 * Prevents checkout when cart total is under $200.
 * Gates both cart drawer AND cart page.
 * 
 * INSTALLATION:
 * 1. Shopify Admin → Online Store → Themes → Edit code
 * 2. Under "Assets", click "Add a new asset" → Create blank file → "min-order-gate.js"
 * 3. Paste this script and Save
 * 4. Open layout/theme.liquid
 * 5. Add this right before </body>:
 *    <script src="{{ 'min-order-gate.js' | asset_url }}" defer></script>
 * 6. Save and test
 * 
 * NOTE: Do NOT install inside snippets/cart-drawer.liquid — load once globally.
 */

(function () {
  'use strict';

  // ===================
  // CONFIG
  // ===================
  const MIN_CENTS = 20000; // $200.00

  // Drawer selectors (confirmed for Trade theme)
  const DRAWER_ROOT_SELECTOR = '#CartDrawer';
  const DRAWER_FORM_SELECTOR = '#CartDrawer-Form';
  const DRAWER_CHECKOUT_BTN_SELECTOR = '#CartDrawer-Checkout';
  const DRAWER_CTAS_SELECTOR = '#CartDrawer .cart__ctas';

  // Cart page selectors (Trade/Dawn patterns)
  // Exclude drawer form to avoid double-matching
  const CART_PAGE_FORM_SELECTOR = 'form[action^="/cart"]:not(#CartDrawer-Form)';
  const CART_PAGE_CHECKOUT_BTN_SELECTORS = [
    'button[name="checkout"]',
    'input[name="checkout"]',
    'button[type="submit"][name="checkout"]',
    'input[type="submit"][name="checkout"]'
  ];
  const CART_PAGE_MESSAGE_INSERT_SELECTORS = [
    '.cart__footer',
    '#main-cart-footer',
    '.cart__ctas',
    'form[action^="/cart"]'
  ];

  // Message IDs
  const MSG_ID_DRAWER = 'min-order-msg-drawer';
  const MSG_ID_CART = 'min-order-msg-cart';

  // Debounce timing
  const DEBOUNCE_MS = 200;

  // ===================
  // LANGUAGE DETECTION
  // ===================
  function isSpanish() {
    // Check Shopify locale, html lang, or URL
    const htmlLang = document.documentElement.lang || '';
    const shopifyLocale = window.Shopify && window.Shopify.locale;
    const urlHasEs = window.location.pathname.startsWith('/es');
    
    return htmlLang.startsWith('es') || shopifyLocale === 'es' || urlHasEs;
  }

  function getMessage(key, values) {
    const messages = {
      en: {
        verifying: 'Verifying cart total...',
        minimum: 'Minimum order:',
        addMore: 'Add {amount} more to checkout.',
        fetchError: "Couldn't verify cart total. Please refresh the page."
      },
      es: {
        verifying: 'Verificando total del carrito...',
        minimum: 'Pedido mínimo:',
        addMore: 'Agregue {amount} más para continuar.',
        fetchError: 'No se pudo verificar el total. Por favor actualice la página.'
      }
    };
    
    const lang = isSpanish() ? 'es' : 'en';
    let msg = messages[lang][key] || messages['en'][key];
    
    if (values) {
      Object.keys(values).forEach(function(k) {
        msg = msg.replace('{' + k + '}', values[k]);
      });
    }
    
    return msg;
  }

  // ===================
  // STATE
  // ===================
  // Pessimistic: start as 'unknown' (blocked) until we verify cart total
  // States: 'unknown' | 'under' | 'ok'
  let gateState = 'unknown';
  let lastTotal = null;
  let isUpdating = false;
  let pendingTimer = null;

  // ===================
  // MONEY FORMAT
  // ===================
  function formatMoneyCents(cents) {
    // Prefer Shopify formatMoney if present
    try {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        const fmt =
          (window.theme && window.theme.moneyFormat) ||
          (window.Shopify && window.Shopify.money_format) ||
          '${{amount}}';
        return window.Shopify.formatMoney(cents, fmt);
      }
    } catch (_) {}

    // Fallback
    return '$' + (cents / 100).toFixed(2);
  }

  // ===================
  // CART FETCH
  // ===================
  async function fetchCartTotal() {
    const res = await fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' }
    });
    if (!res.ok) throw new Error('Failed to fetch /cart.js');
    const cart = await res.json();
    return Number(cart.total_price || 0);
  }

  // ===================
  // DOM HELPERS
  // ===================
  function qs(sel, root) {
    return (root || document).querySelector(sel);
  }

  function qsa(sel, root) {
    return Array.from((root || document).querySelectorAll(sel));
  }

  function ensureMessage(id, insertIntoEl) {
    let el = document.getElementById(id);
    if (el) return el;

    el = document.createElement('div');
    el.id = id;
    el.setAttribute('role', 'alert');
    el.style.display = 'none';
    el.style.width = '100%';
    el.style.boxSizing = 'border-box';
    el.style.margin = '0 0 12px 0';
    el.style.padding = '12px 14px';
    el.style.background = '#dc2626';
    el.style.color = '#ffffff';
    el.style.fontSize = '14px';
    el.style.fontWeight = '600';
    el.style.borderRadius = '6px';
    el.style.textAlign = 'center';
    el.style.lineHeight = '1.35';

    if (insertIntoEl) {
      // Insert as the first child, before any flex/grid items
      insertIntoEl.insertBefore(el, insertIntoEl.firstChild);
      // Force full width in flex containers
      insertIntoEl.style.flexWrap = 'wrap';
    } else {
      // Fallback: try cart form, then body
      const fallback = qs(CART_PAGE_FORM_SELECTOR) || qs('#CartDrawer-Form') || document.body;
      fallback.insertBefore(el, fallback.firstChild);
    }
    return el;
  }

  function setButtonGated(btn, gated) {
    if (!btn) return;
    btn.disabled = !!gated;
    btn.style.opacity = gated ? '0.55' : '';
    btn.style.cursor = gated ? 'not-allowed' : '';
    btn.setAttribute('aria-disabled', gated ? 'true' : 'false');
  }

  function findCartPageCheckoutButtons() {
    const forms = qsa(CART_PAGE_FORM_SELECTOR);
    const buttons = [];

    for (const form of forms) {
      for (const sel of CART_PAGE_CHECKOUT_BTN_SELECTORS) {
        const found = qsa(sel, form);
        for (const b of found) {
          if ((b.getAttribute('name') || '').toLowerCase() === 'checkout') {
            buttons.push(b);
          }
        }
      }
    }

    return Array.from(new Set(buttons));
  }

  function getCartPageMessageContainer() {
    for (const sel of CART_PAGE_MESSAGE_INSERT_SELECTORS) {
      const el = qs(sel);
      if (el) return el;
    }
    return null;
  }

  // ===================
  // APPLY GATE TO UI
  // ===================
  function applyGateToUI() {
    const isBlocked = gateState !== 'ok';
    const isUnknown = gateState === 'unknown';
    const needed = lastTotal !== null ? Math.max(0, MIN_CENTS - lastTotal) : MIN_CENTS;

    // Drawer
    const drawerBtn = qs(DRAWER_CHECKOUT_BTN_SELECTOR);
    if (drawerBtn) {
      const ctas = qs(DRAWER_CTAS_SELECTOR) || drawerBtn.closest('.cart__ctas') || drawerBtn.parentElement;
      const msg = ensureMessage(MSG_ID_DRAWER, ctas);
      setButtonGated(drawerBtn, isBlocked);

      if (isUnknown) {
        msg.innerHTML = '<div>' + getMessage('verifying') + '</div>';
        msg.style.display = 'block';
      } else if (gateState === 'under') {
        msg.innerHTML =
          '<div>' + getMessage('minimum') + ' <strong>' + formatMoneyCents(MIN_CENTS) + '</strong></div>' +
          '<div>' + getMessage('addMore', { amount: '<strong style="text-decoration: underline;">' + formatMoneyCents(needed) + '</strong>' }) + '</div>';
        msg.style.display = 'block';
      } else {
        msg.style.display = 'none';
      }
    }

    // Cart page
    const cartBtns = findCartPageCheckoutButtons();
    if (cartBtns.length) {
      const container = getCartPageMessageContainer();
      const msg = ensureMessage(MSG_ID_CART, container);

      for (const b of cartBtns) setButtonGated(b, isBlocked);

      if (isUnknown) {
        msg.innerHTML = '<div>' + getMessage('verifying') + '</div>';
        msg.style.display = 'block';
      } else if (gateState === 'under') {
        msg.innerHTML =
          '<div>' + getMessage('minimum') + ' <strong>' + formatMoneyCents(MIN_CENTS) + '</strong></div>' +
          '<div>' + getMessage('addMore', { amount: '<strong style="text-decoration: underline;">' + formatMoneyCents(needed) + '</strong>' }) + '</div>';
        msg.style.display = 'block';
      } else {
        msg.style.display = 'none';
      }
    }
  }

  // ===================
  // MAIN UPDATE
  // ===================
  async function refreshGate(force) {
    if (isUpdating) return;
    isUpdating = true;

    try {
      const total = await fetchCartTotal();

      if (!force && total === lastTotal) {
        isUpdating = false;
        return;
      }

      lastTotal = total;
      gateState = total >= MIN_CENTS ? 'ok' : 'under';
      applyGateToUI();
    } catch (err) {
      console.error('[MinOrderGate]', err);
      // Fail closed: if we can't verify, block checkout
      gateState = 'unknown';
      lastTotal = null;
      applyGateToUI();
      // Show error message
      showFetchError();
    } finally {
      isUpdating = false;
    }
  }

  function showFetchError() {
    const errorMsg = getMessage('fetchError');
    const drawerBtn = qs(DRAWER_CHECKOUT_BTN_SELECTOR);
    if (drawerBtn) {
      const msg = document.getElementById(MSG_ID_DRAWER);
      if (msg) {
        msg.innerHTML = '<div>' + errorMsg + '</div>';
        msg.style.display = 'block';
      }
    }
    const cartMsg = document.getElementById(MSG_ID_CART);
    if (cartMsg) {
      cartMsg.innerHTML = '<div>' + errorMsg + '</div>';
      cartMsg.style.display = 'block';
    }
  }

  function debounceRefresh(force) {
    clearTimeout(pendingTimer);
    pendingTimer = setTimeout(function () {
      refreshGate(!!force);
    }, DEBOUNCE_MS);
  }

  // ===================
  // HARD BLOCK AS BACKUP (submit/click capture)
  // ===================
  function blockIfUnderMin(e) {
    // Pessimistic: block if state is unknown OR under
    // Only allow if state is explicitly 'ok'
    if (gateState === 'ok') return;

    const t = e.target;

    // Submit events - only block checkout submissions, not add-to-cart or quantity updates
    if (e.type === 'submit') {
      // Check if this submit was triggered by a checkout button
      // e.submitter is the button/input that triggered the submit
      const submitter = e.submitter;
      const isCheckoutSubmit = submitter && 
        (submitter.name === 'checkout' || submitter.id === 'CartDrawer-Checkout');
      
      // Also check if it's the drawer form being submitted via checkout
      const form = t;
      const isDrawerForm = form && form.matches && form.matches(DRAWER_FORM_SELECTOR);
      
      // Only block if this is actually a checkout submission
      if (isCheckoutSubmit || (isDrawerForm && submitter && submitter.name === 'checkout')) {
        e.preventDefault();
        e.stopPropagation();
        // If unknown, trigger a refresh
        if (gateState === 'unknown') {
          refreshGate(true);
        } else {
          applyGateToUI();
        }
      }
      return;
    }

    // Click events
    if (e.type === 'click') {
      const checkoutEl = t && t.closest
        ? t.closest([
            DRAWER_CHECKOUT_BTN_SELECTOR,
            'button[name="checkout"]',
            'input[name="checkout"]'
          ].join(','))
        : null;

      if (checkoutEl) {
        e.preventDefault();
        e.stopPropagation();
        // If unknown, trigger a refresh
        if (gateState === 'unknown') {
          refreshGate(true);
        } else {
          applyGateToUI();
        }
      }
    }
  }

  // ===================
  // OBSERVE DRAWER ONLY (minimal)
  // ===================
  function setupDrawerObserver() {
    const drawerRoot = qs(DRAWER_ROOT_SELECTOR);
    if (!drawerRoot) return;

    const obs = new MutationObserver(function () {
      debounceRefresh(false);
    });

    obs.observe(drawerRoot, { childList: true, subtree: true });
  }

  // ===================
  // INIT
  // ===================
  function init() {
    // Immediately apply blocked state (pessimistic)
    applyGateToUI();
    
    // Then fetch actual cart total
    refreshGate(true);

    // Backup blockers
    document.addEventListener('submit', blockIfUnderMin, true);
    document.addEventListener('click', blockIfUnderMin, true);

    // Drawer observer
    setupDrawerObserver();

    // Re-check when returning to tab
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') debounceRefresh(true);
    });

    // Re-check on pageshow (handles bfcache)
    window.addEventListener('pageshow', function (e) {
      if (e.persisted) {
        gateState = 'unknown';
        debounceRefresh(true);
      }
    });

    // Re-check after Shopify theme cart updates (Dawn/Trade dispatches events)
    document.addEventListener('cart:refresh', function () {
      debounceRefresh(true);
    });

    console.log('[MinOrderGate] Loaded. Minimum:', formatMoneyCents(MIN_CENTS));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
